import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Check, ArrowRight, ArrowLeft, Calendar as CalIcon, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { VEHICLE_TYPES, PACKAGES, ADDONS, TIME_SLOTS as ALL_TIME_SLOTS, packagePrice, PREMIUM_KEYS } from '../data';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Booking() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(null);
  const [availableTimes, setAvailableTimes] = useState(ALL_TIME_SLOTS);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState(() => {
    const initialPkg = params.get('pkg');
    const validKey = initialPkg && Object.prototype.hasOwnProperty.call(PACKAGES, initialPkg) ? initialPkg : 'regular';
    return {
      vehicle_type: 'sedan',
      package: validKey,
      addons: [],
      preferred_date: '',
      preferred_time: '',
      name: '', phone: '', email: '', address: '', notes: '',
    };
  });

  useEffect(() => { window.scrollTo(0, 0); }, [step, done]);

  // Fire Google Ads conversion once when the booking success screen renders
  useEffect(() => {
    if (done && typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', { send_to: 'AW-18299252513/_nUcCLCpwfUcEKHe4ZVE' });
    }
  }, [done]);

  // Fetch available time slots whenever date changes
  useEffect(() => {
    if (!form.preferred_date) { setAvailableTimes(ALL_TIME_SLOTS); return; }
    setLoadingSlots(true);
    axios.get(`${API}/availability`, { params: { date: form.preferred_date } })
      .then(({ data }) => {
        setAvailableTimes(data.available || []);
        // If selected time is no longer available, clear it
        if (form.preferred_time && !data.available.includes(form.preferred_time)) {
          setForm(f => ({ ...f, preferred_time: '' }));
        }
      })
      .catch(() => setAvailableTimes(ALL_TIME_SLOTS))
      .finally(() => setLoadingSlots(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.preferred_date]);

  const toggleAddon = (id) => {
    setForm((f) => ({ ...f, addons: f.addons.includes(id) ? f.addons.filter(x => x !== id) : [...f.addons, id] }));
  };

  const priceInfo = packagePrice(form.package, form.vehicle_type);
  const basePrice = priceInfo.discounted;
  const originalBase = priceInfo.original;
  const addonPrice = form.addons.reduce((sum, id) => sum + (ADDONS.find(a => a.id === id)?.priceNum || 0), 0);
  const total = basePrice + addonPrice;

  const validateStep = () => {
    if (step === 1) return form.vehicle_type && form.package;
    if (step === 2) return form.preferred_date && form.preferred_time;
    if (step === 3) return form.name && form.phone && form.email && form.address;
    return true;
  };

  const next = () => {
    if (!validateStep()) { toast.error('Please complete required fields.'); return; }
    setStep((s) => Math.min(4, s + 1));
  };
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    try {
      const { data } = await axios.post(`${API}/bookings`, form);
      setDone(data);
      toast.success('Booking received!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit booking. Try again.');
    }
  };

  if (done) {
    return (
      <div data-testid="booking-success" className="pt-32 pb-24 bg-[#0a0a0a] text-white min-h-screen">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <CheckCircle2 className="w-16 h-16 text-blue-400 mx-auto mb-6" />
          <h1 className="font-display font-light text-4xl sm:text-5xl tracking-tight mb-4">Booking Received</h1>
          <p className="text-zinc-400 mb-8">Thanks, {done.name}! We received your request and will confirm shortly by phone or email.</p>
          <div className="bg-[#141414] border border-white/5 rounded-2xl p-6 text-left text-sm space-y-2">
            <div className="flex justify-between"><span className="text-zinc-500">Confirmation ID</span><span className="font-mono text-xs">{done.id}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Package</span><span>{PACKAGES[done.package].name}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Vehicle</span><span>{VEHICLE_TYPES.find(v => v.id === done.vehicle_type)?.label || done.vehicle_type}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Date</span><span>{done.preferred_date} · {done.preferred_time}</span></div>
            <div className="flex justify-between border-t border-white/5 pt-2 mt-2"><span className="text-zinc-500">Estimated total</span><span className="font-display text-xl text-blue-400">${done.estimated_total}</span></div>
          </div>
          <Link to="/" className="mt-8 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 px-7 py-3 rounded-full font-medium">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="pt-32 pb-24 bg-[#0a0a0a] text-white min-h-screen">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <span className="pill mb-4">Book a Detail</span>
        <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter">Reserve your detail in 60 seconds.</h1>

        {/* Steps indicator */}
        <div className="flex items-center gap-2 mt-10 mb-10">
          {[1,2,3,4].map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div data-testid={`step-indicator-${s}`} className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium ${s <= step ? 'bg-blue-600 text-white' : 'bg-white/5 text-zinc-500'}`}>
                {s < step ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 4 && <div className={`flex-1 h-px mx-2 ${s < step ? 'bg-blue-600' : 'bg-white/10'}`} />}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-[#141414] border border-white/5 rounded-2xl p-6 md:p-10">

            {step === 1 && (
              <div>
                <h2 className="font-display text-2xl mb-6">Vehicle & Package</h2>
                <div className="mb-8">
                  <label className="text-xs uppercase tracking-wider text-zinc-500 mb-3 block">Vehicle Type</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {VEHICLE_TYPES.map(v => (
                      <button
                        key={v.id}
                        data-testid={`vehicle-${v.id}`}
                        onClick={() => setForm({ ...form, vehicle_type: v.id })}
                        className={`py-4 px-2 rounded-xl border text-sm transition-all ${form.vehicle_type === v.id ? 'border-blue-500 bg-blue-600/10 text-white' : 'border-white/10 text-zinc-400 hover:border-white/30'}`}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mb-8">
                  <label className="text-xs uppercase tracking-wider text-zinc-500 mb-3 block">Package</label>
                  {['Full Detail', 'Interior Only', 'Exterior Only'].map((cat) => {
                    const catEntries = Object.entries(PACKAGES).filter(([, p]) => p.category === cat);
                    return (
                      <div key={cat} className="mb-5 last:mb-0">
                        <div className="text-[0.7rem] uppercase tracking-[0.2em] text-zinc-500 mb-2">{cat}</div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          {catEntries.map(([key, p]) => {
                            const price = packagePrice(key, form.vehicle_type);
                            return (
                              <button
                                key={key}
                                data-testid={`package-${key}`}
                                onClick={() => setForm({ ...form, package: key })}
                                className={`p-5 rounded-xl border text-left transition-all ${form.package === key ? 'border-blue-500 bg-blue-600/10' : 'border-white/10 hover:border-white/30'}`}
                              >
                                <div className="font-display text-lg">{p.name}</div>
                                <div className="text-xs text-zinc-500 mt-1">
                                  From ${price.discounted}
                                  {price.promo && <span className="text-zinc-600 line-through ml-1">${price.original}</span>}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500 mb-3 block">Add-Ons (optional)</label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {ADDONS.map(a => (
                      <button
                        key={a.id}
                        data-testid={`booking-addon-${a.id}`}
                        onClick={() => toggleAddon(a.id)}
                        className={`p-4 rounded-xl border text-left flex justify-between items-center transition-all ${form.addons.includes(a.id) ? 'border-blue-500 bg-blue-600/10' : 'border-white/10 hover:border-white/30'}`}
                      >
                        <span className="text-sm">{a.label}</span>
                        <span className="text-blue-400 text-sm">{a.price}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="font-display text-2xl mb-6">Date & Time</h2>
                <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Preferred Date</label>
                <input
                  type="date"
                  data-testid="booking-date"
                  min={new Date().toISOString().split('T')[0]}
                  value={form.preferred_date}
                  onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 mb-6 outline-none focus:border-blue-500 text-white"
                />
                <label className="text-xs uppercase tracking-wider text-zinc-500 mb-3 block">Preferred Time {loadingSlots && <span className="text-zinc-600 normal-case ml-2">loading…</span>}</label>
                {!form.preferred_date ? (
                  <p className="text-zinc-500 text-sm">Pick a date to see available time slots.</p>
                ) : availableTimes.length === 0 ? (
                  <p className="text-amber-400 text-sm">No times available on this date. Please choose another date or call us.</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {ALL_TIME_SLOTS.map(t => {
                      const isAvail = availableTimes.includes(t);
                      const selected = form.preferred_time === t;
                      return (
                        <button
                          key={t}
                          data-testid={`time-${t.replace(/[^a-z0-9]/gi, '')}`}
                          disabled={!isAvail}
                          onClick={() => isAvail && setForm({ ...form, preferred_time: t })}
                          className={`py-2.5 text-sm rounded-lg border transition-all ${
                            selected
                              ? 'border-blue-500 bg-blue-600/10 text-white'
                              : isAvail
                                ? 'border-white/10 hover:border-white/30 text-zinc-400'
                                : 'border-white/5 bg-white/[0.02] text-zinc-700 line-through cursor-not-allowed'
                          }`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <div>
                <h2 className="font-display text-2xl mb-6">Your Information</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required placeholder="Full name *" data-testid="booking-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-blue-500" />
                  <input required placeholder="Phone *" data-testid="booking-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-blue-500" />
                </div>
                <input required type="email" placeholder="Email *" data-testid="booking-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-4 w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-blue-500" />
                <input required placeholder="Service address *" data-testid="booking-address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="mt-4 w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-blue-500" />
                <textarea rows={4} placeholder="Additional notes (parking, gate code, vehicle details...)" data-testid="booking-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="mt-4 w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-blue-500 resize-none" />
              </div>
            )}

            {step === 4 && (
              <div>
                <h2 className="font-display text-2xl mb-6">Confirm Your Booking</h2>
                <dl className="space-y-3 text-sm">
                  <Row label="Vehicle" value={VEHICLE_TYPES.find(v => v.id === form.vehicle_type)?.label} />
                  <Row label="Package" value={PACKAGES[form.package].name} />
                  <Row label="Add-ons" value={form.addons.length ? form.addons.map(id => ADDONS.find(a => a.id === id)?.label).join(', ') : 'None'} />
                  <Row label="Date" value={`${form.preferred_date} · ${form.preferred_time}`} />
                  <Row label="Name" value={form.name} />
                  <Row label="Phone" value={form.phone} />
                  <Row label="Email" value={form.email} />
                  <Row label="Address" value={form.address} />
                  {form.notes && <Row label="Notes" value={form.notes} />}
                </dl>
              </div>
            )}

            <div className="flex justify-between mt-10">
              <button onClick={prev} disabled={step === 1} data-testid="booking-prev" className="px-6 py-3 rounded-full border border-white/20 text-zinc-300 hover:bg-white/10 disabled:opacity-30 inline-flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              {step < 4 ? (
                <button onClick={next} data-testid="booking-next" className="px-7 py-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-2 font-medium">
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button onClick={submit} data-testid="booking-submit" className="px-7 py-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-2 font-medium hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]">
                  Confirm Booking <CalIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Sticky summary */}
          <aside className="bg-gradient-to-br from-blue-600/15 to-[#141414] border border-blue-500/30 rounded-2xl p-6 h-fit lg:sticky lg:top-28">
            <h3 className="font-display text-xl mb-4">Order Summary</h3>
            {priceInfo.promo && (
              <div className="mb-4 bg-gradient-to-r from-amber-500/15 to-red-500/15 border border-amber-500/30 rounded-xl px-3 py-2 text-xs text-amber-200 flex items-center gap-2">
                🔥 Limited Time – 20% OFF Premium Detail
              </div>
            )}
            <ul className="space-y-3 text-sm">
              <li className="flex justify-between"><span className="text-zinc-400">Package</span><span>{PACKAGES[form.package].name}</span></li>
              <li className="flex justify-between"><span className="text-zinc-400">Vehicle</span><span>{VEHICLE_TYPES.find(v => v.id === form.vehicle_type)?.label || form.vehicle_type}</span></li>
              <li className="flex justify-between items-baseline">
                <span className="text-zinc-400">Base</span>
                <span>
                  {priceInfo.promo && <span className="text-zinc-500 line-through mr-2">${originalBase}</span>}
                  ${basePrice}
                </span>
              </li>
              <li className="flex justify-between"><span className="text-zinc-400">Add-ons</span><span>${addonPrice}</span></li>
              <li className="flex justify-between border-t border-white/10 pt-3 mt-3 font-display text-lg"><span>Estimated Total</span><span className="text-blue-400">${total}</span></li>
            </ul>
            <p className="text-base text-zinc-300 mt-5 leading-relaxed">
              <span className="text-amber-300">*</span> Final price may vary based on vehicle condition.
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-white/5">
      <span className="text-zinc-500">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}
